import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
} from "@mui/material";
import LiveTvIcon from "@mui/icons-material/LiveTv";
import { CalendarEvent, TrainingSession } from "../api/types.tsx";
import { useEffect, useState } from "react";
import { trainingService } from "../api/service.ts";
import TrainingDetailDialog from "./TrainingDetailDialog.tsx";
import { MatchDialog } from "./MatchDialog.tsx";
import MatchReplayDialog from "./LiveMatch/replay/MatchReplayDialog.tsx";
import { useAuth } from "../AuthContext.tsx";
import MapsButton from "./MapsButton.tsx";
import TravelChip from "./TravelChip.tsx";
import HeadToHeadPanel from "./HeadToHeadPanel.tsx";

interface Props {
  open: boolean;
  onClose: () => void;
  onInsertResult?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onPlayNow?: () => void;
  onReset?: () => void;
  isResuming?: boolean;
  hasLiveSession?: boolean;
  event: CalendarEvent;
}

export default function EventDetailsDialog({
  open,
  onClose,
  onInsertResult,
  onEdit,
  onDelete,
  onPlayNow,
  onReset,
  isResuming = false,
  hasLiveSession = false,
  event,
}: Props) {
  const { isAdmin } = useAuth();
  const isMatch = event.type === "TOURNAMENT" || event.type === "FRIENDLY";
  const isFriendly = event.type === "FRIENDLY";
  const isTraining = event.type === "TRAINING";
  const isBirthday = event.type === "BIRTHDAY";
  const isVisitaMedica = event.type === "VISITA_MEDICA";

  // TRAINING autogenerati (eventId === 0) non sono modificabili/eliminabili
  const isTrainingAutoGenerated = isTraining && event.eventId === 0;

  const [openMatchDetailDialog, setOpenMatchDetailDialog] = useState(false);
  const [openSpectator, setOpenSpectator] = useState(false);
  // allenamento registrato in quella data (stessa scheda dello storico allenamenti)
  const [training, setTraining] = useState<TrainingSession | null>(null);
  const [openTraining, setOpenTraining] = useState(false);

  useEffect(() => {
    setTraining(null);
    if (!open || !isTraining || !event.date) return;
    let alive = true;
    trainingService.getTrainingsBetween(event.date, event.date)
      .then((list: TrainingSession[]) => { if (alive && list.length) setTraining(list[0]); })
      .catch(() => {});
    return () => { alive = false; };
  }, [open, isTraining, event.date]);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: "primary.main" }}>
          {isFriendly ? "Amichevole" : isMatch ? "Match details" : "Training details"}
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={1.5}>
            <Typography variant="body1">
              <strong>Date:</strong> {event.date}
            </Typography>
            <Stack direction="row" spacing={2}>
              <Typography variant="body1">
                <strong>Entrance:</strong> {event.entrance || "-"}
              </Typography>
              <Typography variant="body1">
                <strong>Time:</strong> {event.time || "-"}
              </Typography>
            </Stack>
            <Typography variant="body1">
              <strong>Title:</strong> {event.title}
            </Typography>
            <Typography variant="body1">
              <strong>Result:</strong> {event.result || "-"}
            </Typography>
            <Typography variant="body1">
              <strong>Location:</strong> {event.location}
            </Typography>
            {isMatch && !event.played && <TravelChip travel={event.travel} />}
            {isMatch && !event.played && event.eventId > 0 && <HeadToHeadPanel eventId={event.eventId} />}
            {/* Nuovi campi per TOURNAMENT */}
            {event.phaseName && (
              <Typography variant="body1">
                <strong>Fase:</strong> {event.phaseName}
              </Typography>
            )}
            {event.tournamentGroupName && (
              <Typography variant="body1">
                <strong>Girone:</strong> {event.tournamentGroupName}
              </Typography>
            )}
            <Typography variant="body1">
              <strong>Note:</strong> {event.note || "-"}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions>
          {/* Modifica: solo ADMIN, solo per eventi non giocati e non autogenerati */}
          {onEdit && !isTrainingAutoGenerated && !event.played && isAdmin && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={onEdit}
            >
              Modifica
            </Button>
          )}
          {/* Elimina: solo ADMIN, mai per BIRTHDAY/VISITA_MEDICA/autogenerati */}
          {onDelete && !isTrainingAutoGenerated && !isBirthday && !isVisitaMedica && !event.played && isAdmin && (
            <Button
              variant="outlined"
              color="error"
              onClick={onDelete}
            >
              Elimina
            </Button>
          )}
          {isMatch && !event.played && <MapsButton address={event.location} variant="icon" />}
          {isMatch && !event.played && onPlayNow && isAdmin && (
            <Button
              variant="contained"
              color="success"
              onClick={onPlayNow}
            >
              {isResuming ? 'Continua gara' : 'Gioca ora'}
            </Button>
          )}
          {isMatch && !event.played && isAdmin && (
            <Button
              variant="contained"
              color="primary"
              onClick={onInsertResult}
            >
              Insert result
            </Button>
          )}
          {isMatch && !event.played && hasLiveSession && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<LiveTvIcon />}
              onClick={() => setOpenSpectator(true)}
            >
              Segui live
            </Button>
          )}
          {isMatch && event.played && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setOpenMatchDetailDialog(true);
              }}
            >
              View details
            </Button>
          )}
          {training && (
            <Button variant="contained" color="primary" onClick={() => setOpenTraining(true)}>
              Dettaglio allenamento
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      {openTraining && training && (
        <TrainingDetailDialog training={training} onClose={() => setOpenTraining(false)} />
      )}
      <MatchDialog
        matchId={event.matchId}
        eventId={event.eventId}
        onClose={() => setOpenMatchDetailDialog(false)}
        onReset={onReset ? () => { setOpenMatchDetailDialog(false); onReset(); } : undefined}
        open={openMatchDetailDialog} />
      <MatchReplayDialog
        open={openSpectator}
        onClose={() => setOpenSpectator(false)}
        eventId={event.eventId}
        liveMode
      />
    </>
  );
}